import { useState, useEffect } from 'react';
import { formatTimestamp } from '@/lib/timestampUtils';

/**
 * Hook that returns a live-updating timestamp string
 * Updates every minute for recent timestamps, less frequently for older ones
 */
export function useLiveTimestamp(timestamp: string | Date | null | undefined): string {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!timestamp) return;

    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    if (isNaN(date.getTime())) return;

    // Calculate how often to update based on age
    const getUpdateInterval = () => {
      const diffInSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
      
      // Update every 30 seconds for posts less than 1 hour old
      if (diffInSeconds < 3600) {
        return 30000; // 30 seconds
      }
      // Update every 5 minutes for posts less than 24 hours old
      if (diffInSeconds < 86400) {
        return 300000; // 5 minutes
      }
      // Update every hour for older posts
      return 3600000; // 1 hour
    };

    const interval = setInterval(() => {
      setNow(new Date());
    }, getUpdateInterval());

    return () => clearInterval(interval);
  }, [timestamp]);

  if (!timestamp) return 'Unknown';
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  if (isNaN(date.getTime())) return 'Invalid date';

  return formatTimestamp(date, 'relative');
}

